import { captureException } from '@sentry/react'
import { getAIVideoProjects_API } from 'api'
import { ReduxThunkError } from 'utils/errors'
import { ApiError } from 'utils/errors'
import { cacheSelectors } from 'store/features/cache/selectors'
import { TAppDispatch, TGetState } from 'store/types'
import { IAIVideoGenerationParams, IAIVideoProject } from 'types/aiVideo'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { PROJECT_MEDIA_TYPES } from 'consts'
import {
  getDefaultAIVideoModel,
  getDefaultAIVideoModelGenerationParams,
  apiToStateReferenceMedias,
  buildReferenceMediaFrameWhenMissingDimensions
} from 'store/features/aiVideo/utils'
import { processAIVideoImageUrl } from './utils'
import { withAppThunks } from 'store/with-app-thunks'
import aiVideoSlice from 'store/features/aiVideo/slice'
import { projectSelectors } from 'store/features/project/selectors'
import { aiVideoSelectors } from '../selectors'
const { actions } = aiVideoSlice

const getAIVideoByAssetId = (assetId: number) =>
  withAppThunks(
    appThunks =>
      async (
        dispatch: TAppDispatch,
        getState: TGetState
      ): Promise<IAIVideoProject | null> => {
        const state = getState()
        const aiVideoCache = cacheSelectors.aiVideoCache(state)
        const existingItem = aiVideoCache[assetId]

        if (existingItem) {
          return await processAIVideoImageUrl(existingItem)
        }

        try {
          const { data } = await getAIVideoProjects_API({
            projectIds: [assetId]
          })

          if (data.length === 0) {
            return null
          }

          const [rawProject] = data

          dispatch(appThunks.cacheThunks.addToAiVideoCache(data))

          return await processAIVideoImageUrl(rawProject)
        } catch (error) {
          if (!(error instanceof ApiError)) {
            captureException(new ReduxThunkError(error))
          }
          return null
        }
      }
  )
const setGenerationParams = (params: IAIVideoGenerationParams) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch) => {
    dispatch(actions.setGenerationParams(params))
    dispatch(actions.setHasUserSelectedImageAspectRatio(false))
    if (params.selectedModel) {
      dispatch(
        appThunks.aiVideoThunks.updateSelectedModel({
          modelName: params.selectedModel,
          duration: params.duration,
          quality: params.quality,
          aspectRatio: params.aspectRatio
        })
      )
    }
  })

const setGenerationParamsByAssetId =
  (assetId: number | string) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const numericAssetId = Number(assetId)
    if (isNaN(numericAssetId)) {
      return
    }
    try {
      const data = (await dispatch(
        getAIVideoByAssetId(numericAssetId)
      )) as IAIVideoProject
      if (data) {
        const params: IAIVideoGenerationParams = {
          prompt: data.prompt,
          duration: data.videoDurationSeconds,
          selectedModel: data.videoModel,
          aspectRatio: data.aspectRatio,
          quality: data.quality,
          referenceMedias: apiToStateReferenceMedias(
            data.referenceMedias,
            data.width !== null && data.height !== null
              ? { width: data.width, height: data.height }
              : undefined
          ),
          targetMediaItemId: null,
          aiImageId: data?.meta?.reference?.id || null,
          isConsistencyEnabled: false,
          smartPrompt: '',
          // Preserve the user's current power-mode choice — loading an
          // existing item shouldn't flip the toggle.
          isFastTrack: aiVideoSelectors.isFastTrack(getState()),
          refImageSelectedStyleId: data?.meta?.imageStyleId || undefined,
          parentProjectId: Number(data.id) || undefined
        }
        dispatch(setGenerationParams(params))
      }
    } catch (error) {
      captureException(new ReduxThunkError(error))
    }
  }

const setRegenerationForm =
  (mediaItemId: string) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    try {
      const mediaItems = projectDataSelectors.mediaItems(state)
      const targetMedia = mediaItems.find(
        item =>
          item.id === mediaItemId && item.type === PROJECT_MEDIA_TYPES.aiVideo
      )
      if (!targetMedia) {
        return
      }
      const numericAssetId = Number(targetMedia.assetId)
      let params = getDefaultAIVideoModelGenerationParams()
      if (isNaN(numericAssetId)) {
        params.targetMediaItemId = mediaItemId
      } else {
        const data = (await dispatch(
          getAIVideoByAssetId(Number(targetMedia.assetId))
        )) as IAIVideoProject
        if (!data) {
          return
        }
        const referenceMedias = apiToStateReferenceMedias(
          data.referenceMedias,
          undefined
        )
        if (referenceMedias && referenceMedias?.frames?.length) {
          referenceMedias.frames = await Promise.all(
            referenceMedias.frames.map(
              buildReferenceMediaFrameWhenMissingDimensions
            )
          )
        }
        params = {
          prompt: data.prompt,
          duration: data.videoDurationSeconds,
          selectedModel: data.videoModel,
          aspectRatio: data.aspectRatio,
          quality: data.quality,
          referenceMedias,
          targetMediaItemId: mediaItemId,
          aiImageId: data?.meta?.reference?.id || null,
          isConsistencyEnabled: false,
          smartPrompt: '',
          isFastTrack: aiVideoSelectors.isFastTrack(state),
          refImageSelectedStyleId: data?.meta?.imageStyleId || undefined,
          sourceProjectId: data?.meta?.sourceProjectId,
          parentProjectId: Number(data.id) || undefined
        }
      }
      dispatch(setGenerationParams(params))
    } catch (error) {
      captureException(new ReduxThunkError(error))
    }
  }

const clearGenerationParams = () =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const models = aiVideoSelectors.aiVideoModels(state)
    const defaultSettings = state.aiVideo.defaultGenerationSettings
    const aiFlowVideoModelCode = projectSelectors.aiFlowVideoModelCode(state)
    const aiFlowVideoModel = aiFlowVideoModelCode
      ? models.find(model => model.model === aiFlowVideoModelCode)
      : null
    const defaultModel = getDefaultAIVideoModel(models)

    const modelToUse = aiFlowVideoModel ?? defaultModel
    dispatch(actions.clearGenerationParams(modelToUse))

    if (!aiFlowVideoModel && defaultSettings?.selectedModel) {
      dispatch(
        appThunks.aiVideoThunks.updateSelectedModel({
          modelName: defaultSettings.selectedModel,
          duration: defaultSettings.duration || undefined,
          quality: defaultSettings.quality || undefined,
          aspectRatio: defaultSettings.aspectRatio || undefined
        })
      )
    } else {
      dispatch(
        appThunks.aiVideoThunks.updateSelectedModel({
          modelName: modelToUse.model
        })
      )
    }
  })

export const formThunks = {
  clearGenerationParams,
  getAIVideoByAssetId,
  setGenerationParamsByAssetId,
  setRegenerationForm,
  setGenerationParams
}
